/**
 * Weekday "Add to your routine" row (snippets/wk-cart-upsell.liquid).
 *
 * Loaded unconditionally by snippets/cart-drawer.liquid and sections/main-cart.liquid.
 * The row itself often first appears inside cart HTML that the theme morphs in
 * after an add-to-cart, and <script> tags inserted that way never run, so this
 * file must not live inside the row.
 *
 * - ADD and the arrows use one delegated click listener on document, so they keep
 *   working however many times the cart re-renders the row.
 * - ADD posts the variant to /cart/add.js (quantity 1, one-time, same form fields the
 *   product page sends), then refreshes the cart the theme's normal way by
 *   dispatching a CartLinesUpdateEvent carrying the fresh section HTML. The drawer
 *   stays open and wk-cart-bundle.js's safety net runs on that event as usual.
 * - <wk-cart-upsell> only keeps the arrows' visible/disabled state in sync.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const root = window.Shopify?.routes?.root || '/';

/** Section ids of every cart view on the page, so the add response carries fresh HTML. */
const sectionIds = () => [
  ...new Set(
    [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean)
  ),
];

/** @param {Element} upsell */
const syncArrows = (upsell) => {
  const track = upsell.querySelector('[data-wk-upsell-track]');
  const arrows = upsell.querySelector('[data-wk-upsell-arrows]');
  if (!(track instanceof HTMLElement) || !(arrows instanceof HTMLElement)) return;

  const max = track.scrollWidth - track.clientWidth;
  const overflows = max > 1;
  if (arrows.hidden === overflows) arrows.hidden = !overflows;

  const prev = upsell.querySelector('[data-wk-upsell-prev]');
  const next = upsell.querySelector('[data-wk-upsell-next]');
  if (prev instanceof HTMLButtonElement) prev.disabled = track.scrollLeft <= 1;
  if (next instanceof HTMLButtonElement) next.disabled = track.scrollLeft >= max - 1;
};

/** Scrolls the row by one card (card width + gap). */
const step = (upsell, direction) => {
  const track = upsell.querySelector('[data-wk-upsell-track]');
  const card = track?.querySelector('[data-wk-upsell-item]');
  if (!(track instanceof HTMLElement) || !(card instanceof HTMLElement)) return;
  const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  track.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: reduce ? 'auto' : 'smooth' });
};

/** @param {HTMLButtonElement} button */
const add = async (button) => {
  const item = button.closest('[data-wk-upsell-item]');
  const error = item?.querySelector('[data-wk-upsell-error]');
  const label = button.textContent;
  const variantId = button.dataset.variantId;

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  button.textContent = 'Adding…';
  if (error instanceof HTMLElement) {
    error.hidden = true;
    error.textContent = '';
  }

  try {
    if (!variantId) throw new Error('Sorry, this couldn’t be added to your cart.');

    // Same fields the product page's one-time "Add to cart" posts.
    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');
    const ids = sectionIds();
    if (ids.length) {
      body.append('sections', ids.join(','));
      body.append('sections_url', window.location.pathname);
    }

    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body,
    });
    const added = await response.json();
    if (!response.ok || added.status || added.errors) {
      throw new Error(added.description || added.message || 'Sorry, this couldn’t be added to your cart.');
    }

    const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    const cart = await cartResponse.json();

    // Dispatched from inside the cart view, like the theme's own cart controls, so
    // cart-items-component morphs in the sections we pass and cart-drawer stays open.
    const deferred = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: added.sections,
        items: cart.items,
        itemCount: cart.item_count,
        source: 'wk-cart-upsell',
        didError: false,
      },
    });
  } catch (err) {
    // The Workday: if Shopify won't take it from the cart, link to its page instead of erroring.
    const viewUrl = button.dataset.viewUrl;
    if (viewUrl && button.isConnected) {
      const link = document.createElement('a');
      link.className = button.className;
      link.href = viewUrl;
      link.textContent = 'VIEW';
      link.setAttribute('aria-label', (button.getAttribute('aria-label') || '').replace(/^Add (.*) to cart$/, 'View $1'));
      button.replaceWith(link);
      console.warn('[wk-cart-upsell] Add rejected, showing VIEW instead', err);
      return;
    }
    if (error instanceof HTMLElement) {
      error.textContent = err instanceof Error ? err.message : String(err);
      error.hidden = false;
    }
    console.warn('[wk-cart-upsell] Add failed', err);
  } finally {
    // After a successful add the card is normally re-rendered away; restore it if it's still here.
    if (button.isConnected) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = label;
    }
  }
};

if (!window.__wkCartUpsell) {
  window.__wkCartUpsell = true;

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const upsell = target?.closest('.wk-upsell');
    if (!upsell) return;

    const arrow = target.closest('[data-wk-upsell-prev], [data-wk-upsell-next]');
    if (arrow) {
      event.preventDefault();
      step(upsell, arrow.hasAttribute('data-wk-upsell-next') ? 1 : -1);
      return;
    }

    const button = target.closest('[data-wk-upsell-add]');
    if (!(button instanceof HTMLButtonElement)) return;
    event.preventDefault();
    if (!button.disabled) add(button);
  });

  class WkCartUpsell extends HTMLElement {
    #sync = () => syncArrows(this);
    #resizeObserver = new ResizeObserver(this.#sync);
    // The cart re-render morphs the cards in place, so resync when they change.
    #mutationObserver = new MutationObserver(this.#sync);

    connectedCallback() {
      // Scroll doesn't bubble, so listen in the capture phase for the track's scroll.
      this.addEventListener('scroll', this.#sync, { capture: true, passive: true });
      this.#resizeObserver.observe(this);
      this.#mutationObserver.observe(this, { childList: true, subtree: true });
      this.#sync();
    }

    disconnectedCallback() {
      this.removeEventListener('scroll', this.#sync, { capture: true });
      this.#resizeObserver.disconnect();
      this.#mutationObserver.disconnect();
    }
  }

  if (!customElements.get('wk-cart-upsell')) customElements.define('wk-cart-upsell', WkCartUpsell);
}
